import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogInner,
  DialogTitle,
} from "@/components/ui/dialog";
import { useArchiveProgramMutation } from "@/graphql";
import { MainProgram, MiniProgram } from "@/types/programs";

interface ArchiveProgramDialogProps {
  open: boolean;
  afterArchivedProgram: () => void;
  onClose: () => void;
  program: MainProgram | MiniProgram;
}

export function ArchiveProgramDialog({
  open,
  afterArchivedProgram,
  onClose,
  program,
}: ArchiveProgramDialogProps) {
  const [errorMessage, setErrorMessage] = useState("");
  const { id: programId, name } = program || {};

  const [archiveProgram, { loading }] = useArchiveProgramMutation({
    onCompleted: (data) => {
      const { ok, msg } = data?.archiveProgram || {};
      if (ok) {
        afterArchivedProgram();
        onClose();
      } else {
        setErrorMessage(msg || "Something went wrong. Please try again.");
      }
    },
    onError: (err) => {
      setErrorMessage(err?.message);
    },
    update: (cache, { data }) => {
      if (data?.archiveProgram?.ok) {
        // Invalidate the programs query to force a refetch
        cache.evict({ fieldName: "programs" });
        cache.gc();
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
        if (isOpen) {
          setErrorMessage("");
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive Program Confirmation</DialogTitle>
        </DialogHeader>
        <DialogInner>
          <div>
            <div>
              Are you sure you want to archive <strong>"{name}"</strong>? This
              will:
            </div>
            <ul className="list-disc pl-4 py-2">
              <li>Cancel planned participant invitations</li>
              <li>Restore reserved credits to your workspace account</li>
            </ul>
          </div>

          {errorMessage && (
            <div className="my-2">
              <Callout variant="error">{errorMessage}</Callout>
            </div>
          )}
        </DialogInner>
        <DialogFooter>
          <Button
            intent="danger"
            disabled={loading}
            onClick={() => archiveProgram({ variables: { programId } })}
          >
            Yes, Archive
          </Button>
          <Button intent="secondary" onClick={onClose} disabled={loading}>
            Nevermind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
